import { formatInTimeZone } from "date-fns-tz";
import { haversineDistance } from "@/components/organisms/MyFavourites/FavouriteLocationsTable/suggestions";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";
import { getPhpHostUrl } from "./apiHelpers";

/** rm_tags that mark a location as an airport / terminal where a flight number applies. */
const FLIGHT_TAGS: string[] = [RM_TAGS.Airport, RM_TAGS.InternationalTerminal, RM_TAGS.ShipTerminal];

/**
 * Format a numeric job id into its display form ("J" + the id zero-padded to 6 digits, e.g. 123456 →
 * "J123456", 42 → "J000042") — mirrors the backend `Job::getDisplayJobId` (BookingManager Entity/Job.php).
 * Used to show the booking number on the confirmation page when the create response carries only the
 * numeric id (the guest create endpoint); prefer the response's `display_job_id` when it's present.
 */
export const formatDisplayJobId = (jobId: number): string => `J${String(jobId).padStart(6, "0")}`;

/**
 * Inverse of `formatDisplayJobId` — pull the numeric job id out of a display number ("J123456" → 123456,
 * "J000042" → 42). The operator provider-job list carries only the display `job_number` (no numeric id),
 * so this recovers the id needed to open a booking on the client detail route (`/v2/cust/booking/:jobId`).
 * Returns NaN for an unparseable value; callers should guard.
 */
export const parseDisplayJobId = (displayJobId: string): number => parseInt(displayJobId.replace(/\D/g, ""), 10);

/**
 * Whether a flight number applies to a location — mirrors the mobile
 * `isFlightNumberEnabled` (app-customer src/helpers/bookingHelper.ts): true when the
 * POI carries an airport/terminal rm_tag, OR the address text looks like an airport/terminal.
 */
const FLIGHT_ADDRESS_HINTS = ["airport", "international terminal", "domestic terminal", "terminal", "domestic"];

export const isFlightNumberEnabled = (address?: string | null, rmTags?: string[] | null): boolean => {
  if (rmTags?.some((tag) => FLIGHT_TAGS.includes(tag))) return true;
  if (address) {
    const lower = address.toLowerCase();
    return FLIGHT_ADDRESS_HINTS.some((hint) => lower.includes(hint));
  }
  return false;
};

/**
 * Whether a selected POI is an airport/terminal (drives the Flight Details modal).
 *
 * Leg-neutral by name as well as by behaviour since DEV-1487: the drop-off asks the same question of
 * its own POI to decide whether to capture a DEPARTURE flight, and the rule was never pickup-specific.
 */
export const isAirportPoi = (poi?: PointsOfInterest | null): boolean => (poi ? isFlightNumberEnabled(poi.address, poi.rm_tags) : false);

/** @deprecated Pickup-only name for a leg-neutral predicate — use {@link isAirportPoi}. */
export const isAirportPickup = isAirportPoi;

/**
 * The one terminal regex. Yields the canonical `Terminal <N>` label, or null when there is no terminal.
 *
 * Exported so a caller with free text rather than a POI — a Google prediction's description — matches on
 * the same rule. Note it does NOT accept a compound designator ("2A", "1B", common at LHR/HND): those
 * read as no terminal, which holds the swap rather than moving the pickup somewhere the flight never
 * named. Widening it would change the terminal row this has driven since DEV-1429.
 */
export const terminalFromText = (source: string): string | null => {
  const match = source.match(/\bterminal\s*(?:t)?(\d+|[A-Za-z])\b/i);
  return match ? `Terminal ${match[1].toUpperCase()}` : null;
};

/**
 * The words a POI describes itself with — label, address and city as one string. Google POIs often keep
 * the terminal in the address only, and a hand-typed address may carry its city separately, so every
 * reader below searches the same joined text rather than picking a field.
 */
const poiText = (poi?: PointsOfInterest | null): string => [poi?.rm_label, poi?.address, poi?.city].filter(Boolean).join(" ");

/**
 * Terminal derived from the selected airport POI's name/address (e.g. "…Terminal T1…" → "Terminal 1"),
 * shown inline on the address step. Returns null when the POI carries no terminal — in which case
 * the caller should omit the terminal row entirely.
 */
export const extractTerminal = (pickup?: PointsOfInterest | null): string | null => terminalFromText(poiText(pickup));

/**
 * A flight leg's raw `terminal` ("3", "T3", "I") as the same canonical label `extractTerminal` produces
 * from a POI, so the two can be compared directly. Null for a value that names no terminal (Cirium also
 * returns things like "Domestic"), which is the signal to leave the pickup alone.
 */
export const flightTerminalLabel = (terminal?: string | null): string | null =>
  terminal ? terminalFromText(`Terminal ${terminal}`) : null;

/**
 * IATA code from free text, e.g. "Sydney Airport International Terminal T1 (SYD)" → "SYD". Airport names
 * conventionally carry the code in parentheses; anything else returns null. Three LETTERS exactly, so a
 * terminal written "(T1)" — Google's own name for Perth's — is not mistaken for a code.
 *
 * Text-level twin of {@link terminalFromText}, for the one caller that has a Google prediction rather than
 * a POI: a prediction is a description string, and by the time it is a POI its label has been rewritten.
 */
export const airportIataFromText = (source: string): string | null => source.match(/\(([A-Z]{3})\)/)?.[1] ?? null;

/**
 * IATA code from an airport POI, e.g. "Sydney Airport International Terminal T1 (SYD)" → "SYD".
 * Used to pick the leg landing at the booking airport on multi-leg flights (e.g. QF2 LHR→SIN→SYD).
 */
export const extractAirportIata = (pickup?: PointsOfInterest | null): string | null => airportIataFromText(poiText(pickup));

/**
 * The facts the terminal swap reads off ONE leg of a resolved flight — the arrival leg for a pickup,
 * the departure leg for a drop-off (DEV-1487). Leg-neutral on purpose: a second copy of this shape is
 * how the two swaps drift apart on what counts as "the flight names a terminal".
 */
export type FlightTerminalLeg = {
  iata: string | null;
  /** Airport name, for the Google query and the swapped POI's label. */
  name: string | null;
  /** The leg's raw terminal, as the lookup returned it. */
  terminal: string | null;
  /** Airport city, read only by {@link poiNamesAirport} — a free-text location names a city, not a code. */
  city?: string | null;
};

/** @deprecated Arrival-only name for a leg-neutral shape — use {@link FlightTerminalLeg}. */
export type FlightArrivalTerminal = FlightTerminalLeg;

/**
 * Whether a location already stands at the leg's terminal — same terminal AND same airport. Both halves
 * matter: a pickup at SYD T1 is not at MNL T1. A location at a DIFFERENT airport from the leg is not
 * "not yet at the terminal" either — {@link poiNamesAirport} refuses that swap outright (DCS-403).
 */
export const poiIsAtFlightTerminal = (poi: PointsOfInterest | null | undefined, leg: FlightTerminalLeg): boolean => {
  const label = flightTerminalLabel(leg.terminal);
  if (!label || !leg.iata) return false;
  return extractTerminal(poi) === label && extractAirportIata(poi) === leg.iata;
};

/** @deprecated Pickup-only name for a leg-neutral predicate — use {@link poiIsAtFlightTerminal}. */
export const pickupIsAtFlightTerminal = poiIsAtFlightTerminal;

/**
 * Whether the location's OWN address names the leg's airport — the guard that stops a swap moving a
 * booking somewhere nobody typed (DCS-403).
 *
 * The lookup is only constrained to an airport when the location carries an IATA code. Without one it
 * goes out unconstrained and the server chooses a leg itself, so on a multi-leg number (QF1 is
 * SYD→SIN→LHR) it answered the SINGAPORE leg for a "Sydney Airport" drop-off and the swap rewrote it to
 * Changi. The leg's airport is therefore a GUESS whenever the location cannot vouch for it: a POI with a
 * code is held to that code, and free text has to name the airport's city or its name in its own words.
 * A leg carrying neither is unverifiable from here, so it declines rather than guessing back.
 */
export const poiNamesAirport = (poi: PointsOfInterest | null | undefined, leg: FlightTerminalLeg): boolean => {
  if (!poi) return false;
  // A code is the stronger claim and the one the lookup itself is constrained by, so it settles the
  // question outright — "Melbourne Airport (MEL)" does not name SYD however its text reads.
  const iata = extractAirportIata(poi);
  if (iata) return iata === leg.iata;
  // Whole words only: "Venice" must not vouch for a leg into Nice. Lookarounds rather than `\b`, which is
  // ASCII-only and would split "Zürich" at the umlaut.
  const text = poiText(poi);
  return [leg.city, leg.name].some(
    (name) => !!name && new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, "iu").test(text)
  );
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * How far a terminal swap may move a location, in kilometres.
 *
 * A terminal belongs to the airport the location already names, so the move is a few hundred metres to
 * a few kilometres — DFW's ends, the widest in service, sit about five apart. Fifteen clears every
 * outlier and still catches what this exists for.
 */
export const TERMINAL_SWAP_MAX_KM = 15;

/**
 * A POI's coordinates as numbers, or null when it carries none. (0, 0) is the unset pair, not the Atlantic.
 *
 * Exported so the Places lookup can be anchored on exactly the coordinates {@link poiStaysAtAirport} will
 * judge the answer by — a bias drawn from a point the guard would call absent is a bias around nowhere.
 *
 * COERCED rather than type-tested, though `PointsOfInterest` declares both as numbers. A POI round-trips
 * through the API as a fixed-precision STRING (`clientPoiApi` writes `Number(lat).toFixed(9)`) and the list
 * responses are handed back untransformed, so what the store holds is whatever the server echoed. Nothing
 * else in the app notices — `haversineDistance` and Google both coerce a numeric string silently — which is
 * exactly why a `typeof` test here would be the one place a string mattered, and it would fail OPEN: no
 * anchor on the request and a guard that waves everything through, which is DCS-414 unfixed.
 */
export const poiCoords = (poi?: PointsOfInterest | null): { lat: number; lng: number } | null => {
  // Via a nullish check first, because `Number(null)` and `Number("")` are both 0 — a POI missing ONE of
  // the pair would otherwise read as a real point on the equator rather than as the absent coordinate it is.
  const read = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const lat = read(poi?.lat);
  const lng = read(poi?.lng);
  if (lat === null || lng === null || (lat === 0 && lng === 0)) return null;

  return { lat, lng };
};

/**
 * Whether the POI a swap would commit still stands at the airport it is replacing (DCS-414).
 *
 * {@link poiNamesAirport} asks whether the LEG may be trusted; this asks whether the row finally chosen
 * is the one the leg described, which is a separate claim and the one that failed. The Google fallback
 * matches a prediction on its terminal designator alone and then RELABELS it with the leg's airport, so
 * a wrong hit arrives reading exactly like a right one: prod DCS-414 asked Google for "Adelaide Airport
 * Terminal 1" and took Perth's "Terminal 1, Horrie Miller Drive, Perth Airport WA" — 2,100 km away,
 * relabelled "Adelaide Airport Terminal T1 (ADL)", and priced as the journey's origin. Once committed it
 * even reads as idempotent, because every check downstream parses that label.
 *
 * Coordinates are the only field left that still tells the truth, so the distance is the test.
 * Unmeasurable is not a refusal: a free-text location carries no coordinates, and declining there would
 * drop swaps that were correct before this guard existed — {@link poiNamesAirport} is what covers them.
 */
export const poiStaysAtAirport = (location: PointsOfInterest | null | undefined, candidate: PointsOfInterest): boolean => {
  const from = poiCoords(location);
  const to = poiCoords(candidate);
  if (!from || !to) return true;

  return haversineDistance(from, to) <= TERMINAL_SWAP_MAX_KM;
};

/**
 * The provider POI for a flight leg's terminal, or null. Global airport POIs are one row per terminal
 * ("Sydney Airport Domestic Terminal T3 (SYD)"), so an `airport`-tagged row matching on both terminal and
 * IATA identifies the right one. Reads the same two extractors the pickup itself is described by, so a hit
 * here is the same claim `pickupIsAtFlightTerminal` makes about the pickup afterwards.
 */
export const findProviderTerminalPoi = (leg: FlightTerminalLeg, pois: PointsOfInterest[]): PointsOfInterest | null => {
  const label = flightTerminalLabel(leg.terminal);
  if (!label || !leg.iata) return null;
  return (
    pois.find((poi) => poi.rm_tags?.includes(RM_TAGS.Airport) && extractAirportIata(poi) === leg.iata && extractTerminal(poi) === label) ??
    null
  );
};

/**
 * @deprecated Points at the legacy PHP booking pages. Remaining consumers (BookingTableDetails
 * "Open Booking", the bookings-table job-number link, the trip-log journey link) should migrate to
 * the new operator-scoped routes as their new-UI equivalents land — `getOperatorBookingSheetUrl`
 * already replaces the booking-sheet case.
 */
export const getOldBookingUrl = (accountId: string, bookingId: string, page: string, journey = false) => {
  if (journey) return `${getPhpHostUrl()}/passenger/${accountId}/journey-admin/${page}/${bookingId}`;
  return `${getPhpHostUrl()}/passenger/${accountId}/booking/view/${bookingId}/admin/${page}`;
};

/**
 * The operator-scoped booking sheet for an existing job (`:clientId/booking/:jobId` in
 * routes/operators.tsx) — the sheet resolves view vs edit from the booking's own `is_editable`.
 */
export const getOperatorBookingSheetUrl = (operatorId: string, accountId: string, bookingId: string) => {
  return `${window.location.origin}/dashboard/operator/${operatorId}/clients/${accountId}/booking/${bookingId}`;
};

export const getTripLogGuestViewUrl = (bookingId: string, hash: string) => {
  return `${window.location.origin}/dashboard/trip-log/${bookingId}/${hash}`;
};

export const getBookingDetailsUrl = (operatorId: string, bookingId: string, jobDate: string) => {
  return `${window.location.origin}/dashboard/operator/${operatorId}/bookings?search=${bookingId}&dateFrom=${jobDate}&dateTo=${jobDate}`;
};

/**
 * True when the client answered the wait question at all. Zero is an answer ("meet me at the gate");
 * null is the absence of one.
 *
 * Guards mirror mobile's formatPickupWait exactly, including the finite check. Without it a NaN
 * reaches the join and renders " after landing" — a stray leading space and no number — and an
 * Infinity renders "Infinity hr after landing". Both are only reachable from a malformed payload,
 * which is exactly when a booking page should show nothing rather than nonsense.
 */
const waitWasAnswered = (minutes?: number | null): minutes is number => minutes != null && Number.isFinite(minutes) && minutes >= 0;

/**
 * The numerals alone — "20 min", "1 hr 30 min". Shared by every vocabulary below so the surfaces can
 * disagree about the wording but never about the number. Takes an already-rounded whole number of
 * minutes: callers round before testing for zero, because rounding only the remainder would leave a
 * sub-minute wait (0.4) formatting as the empty string and rendering a bare " after landing".
 */
const waitDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return [hours ? `${hours} hr` : null, mins ? `${mins} min` : null].filter(Boolean).join(" ");
};

/**
 * The client's requested wait before the driver attends the terminal, WITHOUT the "Pickup:" label —
 * "on arrival", "30 min after landing", "1 hr 30 min after landing". Null when the client was never
 * asked (render nothing). Zero is an explicit choice ("meet me at the gate"), NOT an absence.
 *
 * THE "NEVER SAY DELAY" RULE IS REVERSED. Kevin, 26 Aug 2026: "driver delay" is the name of this
 * quantity on every surface, so the client input and the readback stop using different nouns for one
 * number. See `driverDelayCaption`. This docblock keeps the rule rather than deleting it, because a
 * deleted rule reappears as a bug in six months while a reversed one with its reasoning stays understood.
 *
 * The rule as it stood: never say "delay" on client or driver surfaces, because those screens show the
 * flight's OWN lateness as Delayed / Early / On Time (BookingFlightNumber) with no owner named, so two
 * quantities under one word would leave the reader worse off than showing neither — the operator said
 * "delay" only because "requested by client" named the owner.
 *
 * Its premise was over-broad, which is part of why it fell. It claimed EVERY surface showing this also
 * shows a lateness pill. Two of five do not: the rider View Trip screen imports no FlightStatus at all,
 * and the client Booking History's Status column is JOB status (Upcoming / Completed / Cancelled), not
 * the flight's. The collision it guarded against is simply absent there.
 *
 * The accepted cost, recorded deliberately: on the DRIVER screen the collision is real. "incl. 20 min
 * driver delay" sits ~350px above a chip reading "Delayed" (apps/driver FlightStatus, whenever
 * flight_status.delay > 0) — two numbers under one word, the chip's being the flight's lateness and the
 * caption's the client's buffer. Kevin took that cost knowingly in exchange for one name.
 *
 * This function keeps "after landing" regardless: it is the only token saying the wait is counted from
 * the GATE rather than from some other reference.
 */
export const pickupWaitPhrase = (minutes?: number | null): string | null => {
  if (!waitWasAnswered(minutes)) return null;
  const total = Math.round(minutes);
  if (total === 0) return "on arrival";
  return `${waitDuration(total)} after landing`;
};

/**
 * What kind of arrival the STORED pickup was built from, for the operator panel's working.
 *
 * Driven by the backend's `flight_pickup_applied_arrival_source`, never by a live lookup. That
 * distinction is the ticket's opening defect in miniature: the operator row describes the arrival the
 * engine actually used, so inferring it from where the flight is now would print a kind that disagrees
 * with the pickup sitting beside it. (The client booking panel labels a live lookup — a different job,
 * and `arrivalLabel` in flightPickup.ts stays as it is.)
 *
 * Matched on the FAMILY rather than the exact string, because the backend emits raw stored values like
 * `arrival_gate_estimated_utc` and a suffix change should not break the label. An unrecognised source
 * returns null and the caller renders no arrival row at all — a wrong kind is worse than none, and
 * there is no basis for inventing one.
 */
const APPLIED_ARRIVAL_LABELS: [RegExp, string][] = [
  // Gate instants — actual or estimated — are both "At gate": the UX taxonomy keys on which EVENT the
  // value describes, and the review's own worked example labels the gate estimate the engine used
  // ("11:57") as At gate. ArrivalResolver resolves gate instants only, so no runway rung appears here.
  [/gate_actual|gate_estimated/, "At gate"],
  // A published plan, so say so — never imply gate-level precision from a schedule.
  [/gate_scheduled/, "Scheduled arrival"],
  // The creation-time snapshot, used when the engine has never written: also a published plan.
  [/at_booking|creation/, "Scheduled arrival"],
];

export const appliedArrivalLabel = (source?: string | null): string | null =>
  (source && APPLIED_ARRIVAL_LABELS.find(([pattern]) => pattern.test(source))?.[1]) || null;

/**
 * The best-known LIVE arrival on the operator payload, with a phrase naming what kind of arrival it is.
 *
 * This is CONTEXT, not the answer. The working (arrival + delay = pickup) is built from the frozen
 * arrival and must add up at a glance; the live value is worth one deliberate hover when someone is
 * investigating a single booking. Two arrival clocks competing side by side for the same glance is what
 * the UX review rejected — a tooltip is not that.
 *
 * The phrase names the quantity so it cannot be read as a second opinion on the frozen one. Built from
 * one ordered list so the time and its wording cannot drift.
 *
 * The order deliberately DIFFERS from `flightPickup.ts`'s `LANDING_FIELDS`, which is family-first per the
 * DEV-1490 contract and so prefers a gate schedule over a runway actual. This list is tier-first, because
 * its only job is "what is the flight doing now" and a published plan must not outrank a known landing.
 * If that contract precedence is ever revisited, the two lists converge.
 */
const LIVE_ARRIVAL_PHRASES: [keyof BookingFlightLiveArrival, string][] = [
  ["gateActual", "Flight reached the gate at"],
  ["gateEstimated", "Flight is currently estimated at"],
  ["actual", "Flight landed at"],
  ["estimated", "Flight is currently estimated at"],
  ["gateScheduled", "Flight is scheduled at"],
  ["scheduled", "Flight is scheduled at"],
];

type BookingFlightLiveArrival = {
  scheduled?: string | null;
  estimated?: string | null;
  actual?: string | null;
  gateScheduled?: string | null;
  gateEstimated?: string | null;
  gateActual?: string | null;
};

export const liveArrivalDisclosure = (arrival?: BookingFlightLiveArrival | null): { iso: string; prefix: string } | null => {
  if (!arrival) return null;
  for (const [field, prefix] of LIVE_ARRIVAL_PHRASES) {
    const iso = arrival[field];
    if (iso) return { iso, prefix };
  }
  return null;
};

/**
 * The flight's disruption as the word for it, or null when there is nothing to say.
 *
 * `flight_pickup_frozen_reason` is an ENGINE-INTERNAL flag — Job.php's own comment says "Frozen means the
 * BOOKED TIME only… Only the recalculation service reads these". Only the two DISRUPTIONS are facts a
 * client can act on, so only those are surfaced, and as the disruption itself rather than as the freeze.
 *
 * `at_gate` and `no_gate_event` say nothing: they are the ordinary path, and an arrival row that reads "At
 * gate" already covers it. An unrecognised reason also says nothing rather than inventing a status.
 *
 * Returns the reason itself rather than a display label, because the one surface that renders it names the
 * disruption inside a sentence — "Flight QF994 is cancelled" — and a capitalised "Cancelled" cannot be
 * lower-cased back into one without the caller knowing which words are safe to touch. The two values are
 * also the same vocabulary `FlightStatus` uses, so the stored reason and the live status can be compared
 * and reported through one code path.
 */
export const flightDisruptionReason = (reason?: string | null): "cancelled" | "diverted" | null =>
  reason === "cancelled" || reason === "diverted" ? reason : null;

/**
 * Whether two flight numbers name the same flight.
 *
 * The single comparison for this, deliberately: a rehydrated booking can carry the raw payload value
 * ("qf 401") while the capture modal upper-cases and trims, so a naive `===` reads a no-op re-confirm as
 * a change and throws away a pickup that is still valid. Two implementations of that rule is how the two
 * places that need it drift apart.
 */
export const isSameFlightNumber = (a?: string | null, b?: string | null): boolean =>
  (a ?? "").replace(/\s/g, "").toUpperCase() === (b ?? "").replace(/\s/g, "").toUpperCase();

/**
 * The design's provenance caption — "incl. 20 min driver delay" — bound to the pickup time it qualifies
 * and to nothing else. Five of the six Figma nodes use exactly this treatment.
 *
 * Feeds three web surfaces (client Trip Details, client Booking History, operator bookings table), so it
 * is never inlined at a call site: the alignment contract below exists because this one quantity is
 * already carrying several wordings, and a literal in a component is how the number itself forks.
 *
 * "incl." carries the not-additive meaning the copy needs — the pickup time on screen ALREADY contains
 * this wait, and the reader must never be invited to add it. That is a genuine improvement on the
 * deprecated "Pickup: 20 min after landing", which read as an instruction to add.
 *
 * Null at zero, matching `pickupDelayCaption`: "incl. 0 min driver delay" is not a sentence, and no
 * design node shows a zero state. Null when never asked, as everywhere else.
 */
export const driverDelayCaption = (minutes?: number | null): string | null => {
  if (!waitWasAnswered(minutes)) return null;
  const total = Math.round(minutes);
  if (total === 0) return null;
  return `incl. ${waitDuration(total)} driver delay`;
};

/**
 * The OPERATOR's delay as a row VALUE — "20 min requested by client". Used where the panel can show
 * the whole working (Lands + Delay = Booking Time), so the number stands on its own line.
 *
 * Kevin, 25 Aug: the client INPUTS this as a delay, so the operator should SEE it as a delay —
 * breaking that link is how one quantity ends up with two names across screens. The "requested by
 * client" qualifier is load-bearing, not decoration: the same panel renders a Delayed / On Time pill
 * for the flight's OWN lateness, and naming the owner is the only thing keeping the two apart.
 *
 * Null at zero as well as at absent, because neither operator shape renders a zero: with no wait there
 * is no working to reconcile, and a "Delay — None" row beside two identical clocks reads worse than
 * the bare time. Unlike `pickupWaitPhrase`, where zero is a value the client chose.
 */
export const pickupDelayRequestPhrase = (minutes?: number | null): string | null => {
  if (!waitWasAnswered(minutes)) return null;
  const total = Math.round(minutes);
  if (total === 0) return null;
  return `${waitDuration(total)} requested by client`;
};

/**
 * The OPERATOR's caption for a pickup time that already has the wait folded into it — "Includes the
 * 20 min delay requested by client".
 *
 * A whole sentence rather than a value, because the operator panel is display-only: printing the time
 * and the delay as two adjacent numbers left their relationship unstated, and a reader could just as
 * reasonably add them. Stating the relationship is the point; there is nothing left to work out.
 *
 * Kevin, 25 Aug: the client INPUTS this as a delay, so the operator should SEE it as a delay —
 * breaking that link is how one quantity ends up with two names across screens. The "requested by
 * client" qualifier is load-bearing, not decoration: the same panel renders a Delayed / On Time pill
 * for the flight's OWN lateness, and naming the owner is the only thing keeping the two apart.
 *
 * Null when the client was never asked AND when they asked for zero — at zero nothing is folded in,
 * so an "Includes …" sentence would be false, and the bare time needs no explanation because there is
 * no second number to misread. Unlike `pickupWaitPhrase`, zero is an absence here rather than a value.
 */
export const pickupDelayCaption = (minutes?: number | null): string | null => {
  if (!waitWasAnswered(minutes)) return null;
  const total = Math.round(minutes);
  if (total === 0) return null;
  return `Includes the ${waitDuration(total)} delay requested by client`;
};

/**
 * Does `derived arrival + requested delay` still render as the pickup time the reader can see?
 *
 * The single definition of this arithmetic, shared by the operator panel's three-row working and by the
 * operator bookings table's caption. Those surfaces may legitimately differ about what to DO with the
 * answer, never about the answer itself — a second copy of a DST-sensitive comparison is exactly the
 * kind of fork the wordings above exist to prevent.
 *
 * NOT yet applied on the CLIENT summaries, which render the caption gated only on the flight number.
 * The inputs are there (`selectPickupFlightDerivedArrival`, hydrated by `mapBookingDetailToViewDraft`,
 * so the check is possible on the view/edit replay and vacuous on create). It is left out because the
 * client draft holds pickup times as airport wall clocks in a Date's LOCAL fields rather than as an
 * instant plus a timezone, so it needs the convention `DateTimePanel`'s `anchoredWorkingHolds` already
 * uses, not this signature. Until then a client replaying a hand-edited booking can still be told
 * "incl. 30 min driver delay" about a pickup that no longer includes it.
 *
 * Compares the two RENDERED clocks — `arrival + delay` formatted in the booking's timezone against the
 * pickup formatted the same way — so a timezone or format difference can never masquerade as a broken
 * sum. What it is really detecting is a pickup that MOVED after it was derived.
 *
 * Known gap, inherited from the operator panel and not widened here: the delay is added to the arrival
 * INSTANT, so a pair straddling a DST jump still reports as reconciling even though the reader's own
 * addition fails (a 01:50 arrival and an 03:10 pickup labelled "20 min"). Catching that needs wall-clock
 * arithmetic on the displayed arrival, not instant arithmetic. Pinned by a test in
 * `bookingHelpers.test.ts` so it stays visible.
 *
 * False when either input is missing or unusable, so callers must test "do we even hold an arrival"
 * separately: "cannot check" and "checked and it disagrees" are different states and only the second is
 * a reason to suppress a caption.
 *
 * The delay is finite-checked before any date arithmetic. A non-numeric `pickup_delay_minutes` off the
 * wire reaches `new Date(NaN).toISOString()` otherwise, which THROWS rather than returning null — and
 * this now runs once per row inside `generateBookingData`, so one bad row would blank the whole
 * operator bookings table instead of one detail panel.
 */
export const pickupReconcilesAsSum = ({
  arrivalIso,
  delayMinutes,
  pickupIso,
  timezone,
  timeFormat,
}: {
  arrivalIso?: string | null;
  delayMinutes?: number | null;
  pickupIso: string;
  timezone: string;
  timeFormat: string;
}): boolean => {
  if (delayMinutes == null || !Number.isFinite(delayMinutes) || !arrivalIso) return false;
  const arrivalMs = Date.parse(arrivalIso);
  if (Number.isNaN(arrivalMs)) return false;

  const wallClock = (iso: string): string | null => {
    try {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return null;
      return formatInTimeZone(date, timezone, timeFormat);
    } catch {
      return null;
    }
  };

  const sum = wallClock(new Date(arrivalMs + delayMinutes * 60_000).toISOString());
  const pickup = wallClock(pickupIso);
  return sum != null && pickup != null && sum === pickup;
};

/**
 * The same wait as one labelled string — "Pickup: 30 min after landing" — for surfaces that render
 * it as a single line rather than a label/value pair. Matches the driver and rider apps word for
 * word (mobile `formatPickupWait`, app-driver src/helpers/flightHelper.ts); the three surfaces must
 * not drift, so both forms are built from `pickupWaitPhrase` alone.
 */
export const formatPickupWait = (minutes?: number | null): string | null => {
  const phrase = pickupWaitPhrase(minutes);
  return phrase && `Pickup: ${phrase}`;
};
